import React, { useState, useEffect, useRef } from 'react';
import './OrganizationChart.css';
import { Tree, TreeNode } from 'react-organizational-chart';
import { RxCross2 } from "react-icons/rx";
import { getAuthHeaders } from './utils/auth';
import Loader from './components/Loader/Loader';

// Utility function to check if user has admin privileges (admin or super role)
const hasAdminPrivileges = (role) => {
  return role === 'admin' || role === 'super';
};

const GROUP_COLORS = {
  Management: '#3B82F6',
  Staff: '#10B981',
  BOH: '#F59E0B',
  FOH: '#F59E0B'
};

const FALLBACK_GROUP_COLOR = '#6B7280';

const normalizeGroupName = (group) => {
  if (!group) {
    return '';
  }
  if (group === 'Manager') {
    return 'Management';
  }
  return group;
};

const OrganizationChart = ({ userProfile }) => {
  const [orgData, setOrgData] = useState({ nodes: [] });
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [draggedEmployee, setDraggedEmployee] = useState(null);
  const [showHierarchy, setShowHierarchy] = useState(false);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const chartRef = useRef(null);

  const toggleNodeExpand = (e, nodeId) => {
    e.stopPropagation();
    e.preventDefault();
    setExpandedNodes((prev) => {
      const next = { ...prev };
      if (next[nodeId]) delete next[nodeId];
      else next[nodeId] = true;
      return next;
    });
  };

  useEffect(() => {
    const handler = setTimeout(() => {
    fetchOrganizationData();
    }, 300);
    return () => clearTimeout(handler);
  }, []);

  const fetchOrganizationData = async () => {
    try {
      const headers = await getAuthHeaders();
      const url = hasAdminPrivileges(userProfile?.role) ? '/api/organization/structure' : '/api/organization/structure/team';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setOrgData(data);
      } else {
        console.error('Error fetching organization data:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching organization data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Convert flat nodes array to a cycle-safe tree
  const buildTree = (nodes = []) => {
    if (!Array.isArray(nodes) || nodes.length === 0) {
      return [];
    }

    const normalize = (value) => String(value || '').trim().toLowerCase();
    const clonedNodes = nodes.map((node) => ({ ...node, children: [] }));
    const nodeById = new Map();
    const nodeByEmployeeNo = new Map();
    const nodeByName = new Map();
    const parentById = new Map();

    clonedNodes.forEach((node) => {
      nodeById.set(normalize(node.employee_id), node);
      nodeByEmployeeNo.set(normalize(node.employee_no), node);
      nodeByName.set(normalize(node.name), node);
    });

    const findNodeByReference = (reference) => {
      const key = normalize(reference);
      if (!key) {
        return null;
      }
      return nodeById.get(key) || nodeByEmployeeNo.get(key) || nodeByName.get(key) || null;
    };

    const createsCycle = (childId, managerId) => {
      let current = managerId;
      const seen = new Set();
      while (current && !seen.has(current)) {
        if (current === childId) {
          return true;
        }
        seen.add(current);
        current = parentById.get(current);
      }
      return false;
    };

    const setParentIfValid = (child, manager) => {
      if (!child || !manager) {
        return;
      }

      const childId = normalize(child.employee_id);
      const managerId = normalize(manager.employee_id);
      if (!childId || !managerId || childId === managerId) {
        return;
      }

      if (parentById.has(childId)) {
        return;
      }

      if (createsCycle(childId, managerId)) {
        return;
      }

      parentById.set(childId, managerId);
    };

    // First pass: derive parent links from manager direct reports
    clonedNodes.forEach((manager) => {
      if (!Array.isArray(manager.direct_reports)) {
        return;
      }

      manager.direct_reports.forEach((reportRef) => {
        const child = findNodeByReference(reportRef);
        setParentIfValid(child, manager);
      });
    });

    // Second pass: derive parent links from employee reporting_to when missing
    clonedNodes.forEach((employee) => {
      const employeeId = normalize(employee.employee_id);
      if (!employeeId || parentById.has(employeeId)) {
        return;
      }
      const manager = findNodeByReference(
        employee.manager_id || employee.reporting_to
      );
      setParentIfValid(employee, manager);
    });

    // Safety: if any cycle remains, break it by promoting the highest-level node to root.
    const allNodeIds = clonedNodes.map((node) => normalize(node.employee_id));
    allNodeIds.forEach((startId) => {
      if (!startId) {
        return;
      }

      const path = [];
      const pathIndex = new Map();
      let current = startId;
      while (current) {
        if (pathIndex.has(current)) {
          const cycleIds = path.slice(pathIndex.get(current));
          const breakId = cycleIds
            .map((id) => nodeById.get(id))
            .filter(Boolean)
            .sort((a, b) => {
              const levelA = Number(a.hierarchy_level || Number.MAX_SAFE_INTEGER);
              const levelB = Number(b.hierarchy_level || Number.MAX_SAFE_INTEGER);
              if (levelA !== levelB) {
                return levelA - levelB;
              }
              return String(a.employee_no || '').localeCompare(String(b.employee_no || ''));
            })[0]?.employee_id;

          if (breakId) {
            parentById.delete(normalize(breakId));
          }
          break;
        }

        pathIndex.set(current, path.length);
        path.push(current);
        current = parentById.get(current);
      }
    });

    // Build children arrays from parent map
    parentById.forEach((managerId, childId) => {
      const manager = nodeById.get(managerId);
      const child = nodeById.get(childId);
      if (manager && child) {
        manager.children.push(child);
      }
    });

    let roots = clonedNodes.filter((node) => !parentById.has(normalize(node.employee_id)));
    if (roots.length === 0 && clonedNodes.length > 0) {
      roots = [clonedNodes[0]];
    }
    return roots;
  };

  const handleNodeClick = (employee) => {
    setSelectedEmployee(employee);
    fetchEmployeeHierarchy(employee.employee_id);
  };

  const fetchEmployeeHierarchy = async (employeeId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organization/hierarchy/${employeeId}`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setHierarchyData(data);
        setShowHierarchy(true);
      }
    } catch (error) {
      console.error('Error fetching employee hierarchy:', error);
    }
  };

  const handleDragStart = (e, employee) => {
    if (!hasAdminPrivileges(userProfile?.role)) {
      e.preventDefault();
      return;
    }
    setDraggedEmployee(employee);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetEmployee) => {
    e.preventDefault();

    if (!draggedEmployee || !targetEmployee || !hasAdminPrivileges(userProfile?.role)) {
      return;
    }

    if (draggedEmployee.employee_id === targetEmployee.employee_id) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organization/update-hierarchy`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          employee_id: draggedEmployee.employee_id,
          new_manager_id: targetEmployee.employee_id
        })
      });

      if (response.ok) {
        // Refresh organization data
        await fetchOrganizationData();
        alert(`Successfully moved ${draggedEmployee.name} to report to ${targetEmployee.name}`);
      } else {
        alert('Error updating hierarchy');
      }
    } catch (error) {
      console.error('Error updating hierarchy:', error);
      alert('Error updating hierarchy');
    }

    setDraggedEmployee(null);
  };

  const getNodeColor = (group) => {
    const normalizedGroup = normalizeGroupName(group);
    return GROUP_COLORS[normalizedGroup] || FALLBACK_GROUP_COLOR;
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': '#10B981',
      'On Leave': '#F59E0B',
      'Exited': '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  // Custom node renderer for TreeNode
  const renderOrgNode = (node) => {
    const nodeId = String(node.employee_id || '');
    const hasChildren = node.children?.length > 0;
    const isExpanded = !!expandedNodes[nodeId];

    return (
      <div
        className={`org-node ${draggedEmployee?.employee_id === node.employee_id ? 'dragging' : ''}`}
        draggable={hasAdminPrivileges(userProfile?.role)}
        onDragStart={(e) => handleDragStart(e, node)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, node)}
        onClick={() => handleNodeClick(node)}
        style={{
          borderColor: getNodeColor(node.group),
          backgroundColor: node.status === 'Active' ? '#ffffff' : '#f8f9fa'
        }}
      >
        <div className="org-node-header">
          <span className="employee-number">{node.employee_no}</span>
          <span
            className="employee-status"
            style={{ color: getStatusColor(node.status) }}
          >
            {node.status}
          </span>
        </div>
        <div className="employee-name">{node.name}</div>
        <div className="employee-position">{node.position}</div>
        <div className="employee-details">
          <span className="employee-group" style={{ color: getNodeColor(node.group) }}>
            {normalizeGroupName(node.group)}
          </span>
          <span className="employee-grade">Grade {node.grade}</span>
        </div>
        {hasChildren && (
          <button
            type="button"
            className={`org-node-expand-toggle${isExpanded ? ' expanded' : ''}`}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            onClick={(e) => toggleNodeExpand(e, nodeId)}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isExpanded ? '−' : '+'}
          </button>
        )}
      </div>
    );
  };

  // Recursively render the tree
  const renderTree = (node, pathVisited = new Set()) => {
    const nodeId = String(node.employee_id || '');
    if (!nodeId || pathVisited.has(nodeId)) {
      return null;
    }

    const nextPathVisited = new Set(pathVisited);
    nextPathVisited.add(nodeId);

    return (
      <TreeNode key={node.employee_id} label={renderOrgNode(node)}>
        {expandedNodes[nodeId] && node.children?.length > 0 &&
          node.children.map((child) => renderTree(child, nextPathVisited))}
      </TreeNode>
    );
  };

  if (loading) {
    return <Loader />
  }

  const roots = buildTree(orgData.nodes);

  return (
    <div className="organization-chart w-full min-w-0 max-w-full">
      <div className="org-chart-header">
        <h2>Organization Structure</h2>
        {hasAdminPrivileges(userProfile?.role) && (
          <div className="org-chart-controls">
            <p className="drag-help">💡 Drag and drop employees to change reporting structure</p>
            <button onClick={fetchOrganizationData} className="refresh-button">
              🔄
            </button>
          </div>
        )}
      </div>

      <div className="org-chart-container" ref={chartRef}>
        {roots.length > 0 && (
          <div>
            {roots.map(root => (
              <Tree
                key={root.employee_id}
                lineWidth={'2px'}
                lineColor={'#94A3B8'}
                lineBorderRadius={'10px'}
                label={renderOrgNode(root)}
              >
                {expandedNodes[String(root.employee_id)] &&
                  root.children.map(child => renderTree(child))}
              </Tree>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="org-chart-legend">
        <h3>Legend</h3>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: GROUP_COLORS.FOH }}></span>
            FOH (Front of House)
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: GROUP_COLORS.Management }}></span>
            Management
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: GROUP_COLORS.Staff }}></span>
            Staff
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: GROUP_COLORS.BOH }}></span>
            BOH (Back of House)
          </div>
        </div>
      </div>

      {/* Employee Hierarchy Modal */}
      {showHierarchy && hierarchyData && (
        <div className="hierarchy-modal ">
          <div className="hierarchy-modal-content rounded-lg overflow-hidden bg-white shadow-lg min-w-0">
            <div className="hierarchy-header shadow-md sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold p-4">Employee Hierarchy</h3>
              <RxCross2
                onClick={() => setShowHierarchy(false)}
                className='close-button absolute top-[21px] right-4 text-gray-500 hover:text-gray-700'
              />

            </div>

            <div className="overflow-y-auto overflow-x-hidden min-h-0 flex-1 max-h-[min(60vh,calc(100dvh-14rem))] w-[95%] max-w-full mx-auto relative">
              <div className="hierarchy-content p-4">
                {/* Upward Hierarchy */}
                <div className="hierarchy-section mb-6">
                  <h4 className="text-md font-medium mb-2">Reports To</h4>
                  {hierarchyData.upward_hierarchy.length > 0 ? (
                    <div className="hierarchy-chain space-y-2">
                      {hierarchyData.upward_hierarchy.map((manager, index) => (
                        <div key={manager.id} className="hierarchy-item">
                          <span className="hierarchy-level text-sm text-gray-500">Level {index + 1}</span>
                          <span className="hierarchy-name font-medium">{manager.name}</span>
                          <span className="hierarchy-position text-sm text-gray-600">{manager.position}</span>
                          <span className="hierarchy-employee-no text-sm text-gray-500">{manager.employee_no}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No managers above this employee</p>
                  )}
                </div>

                {/* Current Employee */}
                <div className="hierarchy-section current-employee mb-6">
                  <h4 className="text-md font-medium mb-2">Current Employee</h4>
                  <div className="hierarchy-item highlighted sm:flex-row flex-col sm:items-center items-start">
                    <span className="hierarchy-name font-medium">{hierarchyData.employee.name}</span>
                    <span className="hierarchy-position text-sm text-gray-600">{hierarchyData.employee.position}</span>
                    <span className="hierarchy-employee-no text-sm text-gray-500">{hierarchyData.employee.employee_no}</span>
                  </div>
                </div>

                {/* Downward Hierarchy */}
                <div className="hierarchy-section">
                  <h4 className="text-md font-medium mb-2">Direct Reports</h4>
                  {hierarchyData.downward_hierarchy.length > 0 ? (
                    <div className="hierarchy-chain space-y-2">
                      {hierarchyData.downward_hierarchy.map((report) => (
                        <div key={report.id} className="hierarchy-item sm:flex-row flex-col sm:items-center items-start">
                          <span className="hierarchy-name font-medium">{report.name}</span>
                          <span className="hierarchy-position text-sm text-gray-600">{report.position}</span>
                          <span className="hierarchy-employee-no text-sm text-gray-500">{report.employee_no}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No direct reports</p>
                  )}
                </div>
              </div>
            </div>
            <div className="hierarchy-footer shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] sticky bottom-0 bg-white z-10 p-4">
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationChart;